import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpRight, ShieldCheck, RotateCcw } from 'lucide-react';
import { nominatorApi, useNominatorAuth } from '@/context/NominatorAuthContext';
import { useRecaptchaToken, recaptchaHeaders, RecaptchaActions } from '@/lib/recaptcha';
import usePageTitle from '@/hooks/usePageTitle';

/**
 * Nominator Registration + Sign-In (Passwordless OTP)
 *
 * Restored flow (matches the original pre-passwordless UX):
 *   1. Registration form — Company Name, Full Name, Email, Contact Number
 *      (Returning users can toggle to a compact "sign in with email only"
 *      view via the link at the bottom.)
 *   2. Email OTP verification — 6-digit code sent to the registered email.
 *   3. On success → redirect straight to the nomination form (/portal/new).
 *
 * Backend contract (`POST /api/nominator/otp/start`):
 *   - First-time email: requires `name`, `mobile`, `company` in the payload.
 *   - Returning email: extra fields are silently ignored, stored profile
 *     always wins.
 */
const NominatorLogin = () => {
  usePageTitle('Register');
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useNominatorAuth();
  const executeRecaptcha = useRecaptchaToken();

  const [mode, setMode] = useState('register');      // 'register' | 'signin'
  const [step, setStep] = useState('form');          // 'form' | 'otp'
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ company: '', name: '', email: '', mobile: '' });
  const [session, setSession] = useState(null);
  const [otp, setOtp] = useState('');

  // Always land verified users on the dashboard. The nomination form is
  // reachable only via an explicit "Start New Nomination" click from inside
  // the portal — never via a direct link from the public site.
  const nextPath = location.state?.from && location.state.from.startsWith('/portal/') && location.state.from !== '/portal/new'
    ? location.state.from
    : '/portal/dashboard';

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error('Please enter a valid email address.');
      return false;
    }
    if (mode === 'register') {
      if (!form.company.trim()) { toast.error('Please enter your Company / Agency name.'); return false; }
      if (!form.name.trim())    { toast.error('Please enter your full name.'); return false; }
      if (!form.mobile.trim())  { toast.error('Please enter your contact number.'); return false; }
    }
    return true;
  };

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    if (!validate()) return;
    setLoading(true);
    try {
      const body = { email: form.email.trim().toLowerCase() };
      if (mode === 'register') {
        body.name = form.name.trim();
        body.mobile = form.mobile.trim();
        body.company = form.company.trim();
      }
      const token = await executeRecaptcha(RecaptchaActions.otp);
      const { data } = await nominatorApi.post('/nominator/otp/start', body, {
        headers: recaptchaHeaders(token, RecaptchaActions.otp),
      });
      setSession(data.session_id);
      setStep('otp');
      toast.success(data.delivered
        ? `Verification code sent to ${form.email.trim()}.`
        : 'Code generated. If email is delayed, try resending.');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // The backend asks for name/mobile/company only when the email is new.
      // If a "sign-in" attempt fires that error, gracefully switch to register.
      if (typeof detail === 'string' && detail.toLowerCase().includes('first-time')) {
        setMode('register');
        toast.info("Looks like you're new here. Please complete the registration form to continue.");
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Could not send verification code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (!otp || otp.length < 4) {
      toast.error('Enter the 6-digit code from your inbox.');
      return;
    }
    setLoading(true);
    try {
      await nominatorApi.post('/nominator/otp/verify', { session_id: session, code: otp.trim() });
      const n = await refresh();
      if (!n) throw new Error('Session did not attach. Please try again.');
      toast.success('Verified. Opening your nomination form…');
      navigate(nextPath, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not verify code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep('form');
    setOtp('');
    setSession(null);
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)] flex items-center justify-center px-4 py-12" data-testid="nominator-login">
      <div className="w-full max-w-2xl">
        <div className="max-w-2xl mx-auto bg-white border border-[#B8884A]/35 shadow-[0_24px_60px_-30px_rgba(58,36,24,0.25)]">
          <div className="border-b border-[#B8884A]/25 bg-[var(--bg-deep)]/40 px-6 md:px-10 py-8">
            <div className="text-[10px] tracking-[0.35em] uppercase text-[var(--gold)]">The AdAura Awards 2026</div>
            <h1 className="font-serif text-[var(--ink)] text-3xl md:text-4xl tracking-[-0.01em] leading-tight mt-3">
              {step === 'otp'
                ? <>Verify <span className="italic text-[var(--gold)]">Email</span></>
                : mode === 'register'
                  ? <>Register <span className="italic text-[var(--gold)]">Now</span></>
                  : <>Sign <span className="italic text-[var(--gold)]">In</span></>}
            </h1>
            <p className="font-serif text-[var(--ink)]/85 text-[14.5px] md:text-base leading-[1.7] mt-4">
              {step === 'otp'
                ? `Enter the 6-digit code we just sent to ${form.email}.`
                : mode === 'register'
                  ? 'Create your nominator account to submit your entry. Takes under a minute.'
                  : 'Enter your registered email to receive a verification code.'}
            </p>
          </div>

          {step === 'form' && mode === 'register' && (
            <form onSubmit={sendOtp} className="px-6 md:px-10 py-8 space-y-5" data-testid="register-form">
              <Row label="Company / Agency Name *">
                <input type="text" value={form.company} onChange={(e) => set('company', e.target.value)}
                  className="field-input" data-testid="register-company" autoComplete="organization"
                  placeholder="e.g. Marketing Mind" />
              </Row>
              <Row label="Full Name *">
                <input type="text" value={form.name} onChange={(e) => set('name', e.target.value)}
                  className="field-input" data-testid="register-name" autoComplete="name" />
              </Row>
              <Row label="Email Address *">
                <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
                  className="field-input" data-testid="register-email" autoComplete="email" />
              </Row>
              <Row label="Contact Number *">
                <input type="tel" value={form.mobile} onChange={(e) => set('mobile', e.target.value)}
                  className="field-input" data-testid="register-mobile" autoComplete="tel"
                  placeholder="+91 9xxxx xxxxx" />
              </Row>
              <button type="submit" disabled={loading}
                className="btn-solid w-full justify-center disabled:opacity-50"
                data-testid="register-send-otp">
                {loading ? 'Sending…' : (<>Send OTP to Email <ArrowUpRight size={14} /></>)}
              </button>
              <p className="text-[11px] text-[var(--ink-muted)] leading-relaxed text-center">
                Your details are used only to verify the nomination and never shared.
              </p>
              <div className="pt-3 border-t border-[#B8884A]/20 text-center">
                <button type="button" onClick={() => setMode('signin')}
                  className="text-[11px] tracking-[0.28em] uppercase text-[var(--ink-muted)] hover:text-[var(--gold)]"
                  data-testid="switch-to-signin">
                  Already registered? Sign in with email
                </button>
              </div>
            </form>
          )}

          {step === 'form' && mode === 'signin' && (
            <form onSubmit={sendOtp} className="px-6 md:px-10 py-8 space-y-5" data-testid="signin-form">
              <Row label="Email Address *">
                <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)}
                  className="field-input" data-testid="signin-email" autoComplete="email" autoFocus />
              </Row>
              <button type="submit" disabled={loading}
                className="btn-solid w-full justify-center disabled:opacity-50"
                data-testid="signin-send-otp">
                {loading ? 'Sending…' : (<>Send OTP to Email <ArrowUpRight size={14} /></>)}
              </button>
              <div className="pt-3 border-t border-[#B8884A]/20 text-center">
                <button type="button" onClick={() => setMode('register')}
                  className="text-[11px] tracking-[0.28em] uppercase text-[var(--ink-muted)] hover:text-[var(--gold)]"
                  data-testid="switch-to-register">
                  New here? Register instead
                </button>
              </div>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={verifyOtp} className="px-6 md:px-10 py-8 space-y-5" data-testid="otp-form">
              <div className="text-center">
                <span className="inline-flex w-12 h-12 items-center justify-center border border-[var(--gold)] text-[var(--gold)]">
                  <ShieldCheck size={20} />
                </span>
                <div className="text-[var(--ink)] text-sm mt-4">
                  Code sent to <span className="font-semibold">{form.email}</span>
                </div>
              </div>
              <Row label="Enter 6-digit OTP *">
                <input type="text" inputMode="numeric" maxLength={6}
                  value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className="field-input text-center tracking-[0.5em] text-xl numeral"
                  data-testid="otp-input" autoFocus />
              </Row>
              <button type="submit" disabled={loading || otp.length < 4}
                className="btn-solid w-full justify-center disabled:opacity-50"
                data-testid="otp-verify">
                {loading ? 'Verifying…' : (<>Verify &amp; Continue <ArrowUpRight size={14} /></>)}
              </button>
              <button type="button" onClick={restart}
                className="w-full inline-flex items-center justify-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[var(--ink-muted)] hover:text-[var(--gold)] transition-colors"
                data-testid="otp-resend">
                <RotateCcw size={12} /> Didn&apos;t get it? Change details &amp; resend
              </button>
            </form>
          )}

          <p className="text-[11px] text-[var(--ink-soft)] px-6 md:px-10 pb-8 leading-relaxed">
            By continuing you agree to our <a href="/terms" className="underline">Terms</a>, <a href="/privacy" className="underline">Privacy Policy</a>, and <a href="/entrant-consent" className="underline">Entrant Consent</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#6B4F38] font-semibold mb-2">{label}</span>
    {children}
  </label>
);

export default NominatorLogin;
